import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { useState } from "react";
import {
  convertToCurrencyFormat,
  getSourceItemParentQuantity,
  isManagementUser,
} from "../../../shared/utility";
import OrderSubtable1 from "./subtables/OrderSubtable1";
import TraceableBarcode from "../../controls/TraceableBarcode";

export default function OrderProductsTable(props) {
  const {
    loading,
    values,
    onItemEditComplete,
    closed,
    setEmptyModelOpen,
    showErrorModal,
    onCreatePriceSplit,
    onUpdatePriceSplit,
    onDeletePriceSplit,
    onUpdateSourceItemTax,
    pricingLocked,
    onBulkUpdateSourceItemTaxIncluded,
    taxRateOptions,
  } = props;

  const [expandedRows, setExpandedRows] = useState(null);

  const allowExpansion = (rowData) => {
    return rowData.source_items.length > 0;
  };

  const rowExpansionTemplate = (rowData) => {
    return (
      <OrderSubtable1
        data={rowData.source_items}
        parentItemsLocked={Number(rowData.is_returned) === 1 || Number(rowData.credit_return_id) > 0}
        onItemEditComplete={onItemEditComplete}
        closed={closed}
        setEmptyModelOpen={setEmptyModelOpen}
        showErrorModal={showErrorModal}
        onCreatePriceSplit={onCreatePriceSplit}
        onUpdatePriceSplit={onUpdatePriceSplit}
        onDeletePriceSplit={onDeletePriceSplit}
        onUpdateSourceItemTax={onUpdateSourceItemTax}
        pricingLocked={pricingLocked}
        onBulkUpdateSourceItemTaxIncluded={onBulkUpdateSourceItemTaxIncluded}
        taxRateOptions={taxRateOptions}
      />
    );
  };

  const subtotalBody = (rowData) => {
    return <span>{convertToCurrencyFormat(rowData.subtotal_price, 2)}</span>;
  };

  const taxAmountBody = (rowData) => {
    return <span>{convertToCurrencyFormat(rowData.tax_amount, 2)}</span>;
  };
  const productNameBody = (rowData) => {
    const sourceItems = (rowData.source_items || []).filter(
      (item) => getSourceItemParentQuantity(item) > 0.0001,
    );
    const plus = new Set(
      sourceItems.map((item) => String(item.manufactured_product?.plu ?? "")),
    );
    const singlePlu = Number(rowData.is_full_carton) === 1 && plus.size === 1;

    if (singlePlu) {
      const productName =
        sourceItems[0]?.manufactured_product?.product?.name || "";
      const plu = sourceItems[0]?.manufactured_product?.plu || "";

      return (
        <div>
          {productName} <br /> <span className="font-bold">PLU: {plu}</span>
        </div>
      );
    }

    return <p>Mixed Carton</p>;
  };

  const barcodeBody = (rowData) => {
    return (
      <TraceableBarcode
        barcode={rowData.barcode}
        historyType="mixed-carton"
      />
    );
  };

  const rowStyleClass = (data) => {
    if (data.credit_return_id > 0) {
      return { "bg-cyan-100": true };
    } else if (data.is_returned === 0) {
      return { "bg-gray-100": true };
    } else if (data.is_returned === 1) {
      return { "bg-yellow-100": true };
    }
    return {};
  };

  const isManagement = isManagementUser();

  const pricingColumns = isManagement
    ? [
        {
          field: "subtotal_price",
          header: "Subtotal Price",
          body: subtotalBody,
        },
        {
          field: "tax_amount",
          header: "Subtotal Tax",
          body: taxAmountBody,
        },
      ]
    : [];

  return (
    <div className="">
      <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
        <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
          {loading ? (
            <div>
              <div className="border h-10 shadow rounded-md p-4 w-full mx-auto">
                <div className="animate-pulse flex space-x-4">Loading</div>
              </div>
            </div>
          ) : (
            <div className="overflow-hidden">
              <DataTable
                value={values.mixed_cartons}
                expandedRows={expandedRows}
                onRowToggle={(e) => {
                  setExpandedRows(e.data);
                }}
                rowExpansionTemplate={rowExpansionTemplate}
                dataKey="id"
                tableClassName="min-w-full divide-y divide-gray-300 create-order-inventory-table-parent"
                cellClassName="p-2 border-1-bottom-gray text-sm align-middle"
                rowClassName={rowStyleClass}
              >
                <Column header="Product Name" body={productNameBody} />
                <Column header="Barcode" body={barcodeBody} />
                <Column
                  header="Shipped by"
                  field="shipped_by_name"
                  headerClassName="order-products-center-column"
                  bodyClassName="order-products-center-column"
                />
                {pricingColumns.map((col) => (
                  <Column
                    key={col.field}
                    header={col.header}
                    field={col.field}
                    body={col.body}
                    headerClassName="order-products-money-column"
                    bodyClassName="order-products-money-column"
                  />
                ))}
                <Column
                  expander={allowExpansion}
                  headerClassName="order-products-expander-column"
                  bodyClassName="order-products-expander-column"
                />
              </DataTable>
            </div>
          )}
          <div className="mt-6 flex justify-end">
            <div className="w-full max-w-sm">
              <div className="space-y-2">
                {isManagement ? (
                  <>
                    <div className="flex justify-between items-center">
                      <span className="text-md font-medium text-gray-600">
                        Subtotal:
                      </span>
                      <span className="text-md font-semibold text-gray-900">
                        {convertToCurrencyFormat(
                          values.subtotal_dollar_amount,
                          2,
                        )}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-md font-medium text-gray-600">
                        Tax:
                      </span>
                      <span className="text-md font-semibold text-gray-900">
                        {convertToCurrencyFormat(values.tax_amount, 2)}
                      </span>
                    </div>
                    <div className="border-t border-gray-300 pt-2 mt-2">
                      <div className="flex justify-between items-center">
                        <span className="text-lg font-bold text-gray-900">
                          Total:
                        </span>
                        <span className="text-lg font-bold text-gray-900">
                          {convertToCurrencyFormat(values.total_dollar_amount, 2)}
                        </span>
                      </div>
                    </div>
                  </>
                ) : null}
                <div className="flex justify-between items-center">
                  <span className="text-lg font-bold text-gray-900">
                    Total Cartons:
                  </span>
                  <span className="text-lg font-bold text-gray-900">
                    {values.total_cartons} cartons
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
